import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { StringSchema } from 'yup';
import { EDevice } from '../../../../redux/sequence/enums';
import { RootState } from '../../../../redux/types';
import { PrivateCustomer } from '../../../../types/instant-lease-api';
import {
  formatNumberWithThousandsSeparator,
  stripToDigits,
} from '../../../../utils/number-format';
import Button from '../../../b2b/components/button/button';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import FormLayout from '../../../common/form-layout/form-layout';
import InputField from '../../../common/input-field/input-field';

interface IncomeTemplateDataProps {
  onSubmit: (owner: IncomeTemplateData) => void;
  tabButton: boolean;
  privateCustomer: PrivateCustomer | undefined;
  loading?: boolean;
}

export interface IncomeTemplateData {
  income: string;
  housing_cost: string;
  additional_income?: string | undefined;
  additional_cost?: string | undefined;
}

const IncomeData: React.FC<IncomeTemplateDataProps> = ({
  onSubmit,
  tabButton,
  privateCustomer,
  loading,
}) => {
  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // Employment type
  const employmentType = privateCustomer?.living_details?.employment_type;

  // Residence type
  const residenceType = privateCustomer?.living_details?.residence_type;

  const withCostMatcher = <T extends string | undefined>(
    schema: StringSchema<T, any, any, any>,
  ) =>
    schema.test({
      message:
        'Gültiges Format: positive ganze Zahlen ohne Tausendertrennzeichen',
      test: (value) =>
        !value || schema.matches(/^(0|[1-9]\d*)$/).isValidSync(value),
    });

  // validation Schema
  const validationSchema = yup.object().shape({
    income: withCostMatcher(yup.string().required('Pflichtfeld')),
    housing_cost: withCostMatcher(yup.string().required('Pflichtfeld')),
    additional_income: withCostMatcher(yup.string()),
    additional_cost: withCostMatcher(yup.string()),
  });

  // Form Hook
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<IncomeTemplateData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as Resolver<IncomeTemplateData>,
    defaultValues: {
      income: privateCustomer?.income_and_cost_details?.income?.units,
      additional_income:
        privateCustomer?.income_and_cost_details?.additional_income?.units,
      housing_cost:
        privateCustomer?.income_and_cost_details?.housing_cost?.units,
      additional_cost:
        privateCustomer?.income_and_cost_details?.additional_cost?.units,
    },
  });

  // Change the sub title depending on employment type of the user
  const getSubtitleIncome = () => {
    if (
      employmentType === 'EMPLOYMENT_TYPE_JOB_SEEKER' ||
      employmentType === 'EMPLOYMENT_TYPE_HOUSEWIFE' ||
      employmentType === 'EMPLOYMENT_TYPE_INTERN' ||
      employmentType === 'EMPLOYMENT_TYPE_SELF_EMPLOYED' ||
      employmentType === 'EMPLOYMENT_TYPE_STUDENT'
    ) {
      return 'Einkünfte aus Ihrem Einkommen';
    }
    if (employmentType === 'EMPLOYMENT_TYPE_PENSIONER') {
      return 'Einkünfte aus Ihrer Rente';
    }

    return 'Einkünfte aus Ihrem Gehalt';
  };

  // Change the sub title depending on employment type of the user
  const getSubtitleRent = () => {
    if (
      residenceType === 'RESIDENCE_TYPE_APARTMENT_OWNER' ||
      residenceType === 'RESIDENCE_TYPE_HOUSE_OWNER'
    ) {
      return 'Wohnnebenkosten';
    }

    return 'Warmmiete';
  };

  // OnSubmit
  const submitData = (data: IncomeTemplateData) => {
    onSubmit({
      income: data.income,
      additional_income: data.additional_income,
      housing_cost: data.housing_cost,
      additional_cost: data.additional_cost,
    });
  };

  return (
    <form className='left' onSubmit={handleSubmit(submitData)}>
      <FormLayout sectionTitle='Monatlichen Nettoeinnahmen'>
        <Controller
          name='income'
          control={control}
          render={({ field }) => (
            <InputField
              additionalText='€ | Monat'
              hint='Damit sind die regelmäßigen Einnahmen gemeint, die du durch deine berufliche 
          Tätigkeit erzielst – also dein monatliches Nettoeinkommen aus deinem Angestelltenverhältnis.'
              label='Einkünfte aus dem Gehalt'
              type='text'
              inputMode='numeric'
              tooltip={getSubtitleIncome()}
              error={errors.income?.message}
              tooltipPosition='inline-block'
              {...field}
              value={formatNumberWithThousandsSeparator(field.value)}
              onChange={(e) => field.onChange(stripToDigits(e.target.value))}
            />
          )}
        />
        <p className='baseline-tight b2c' />
        <Controller
          name='additional_income'
          control={control}
          render={({ field }) => (
            <InputField
              additionalText='€ | Monat'
              label='Sonstige Einnahmen'
              hint='Sonstige Einnahmen sind alle Einkünfte, die nicht aus deinem Gehalt stammen. 
          Dazu gehören zum Beispiel Mieteinnahmen, Unterhaltszahlungen, Renten, Kapitalerträge oder staatliche 
          Leistungen wie Kindergeld oder Elterngeld.'
              optional
              placeholder='optional'
              type='text'
              inputMode='numeric'
              error={errors.additional_income?.message}
              {...field}
              value={formatNumberWithThousandsSeparator(field.value)}
              onChange={(e) => field.onChange(stripToDigits(e.target.value))}
            />
          )}
        />
      </FormLayout>

      <FormLayout sectionTitle='Monatlichen Ausgaben'>
        <Controller
          name='housing_cost'
          control={control}
          render={({ field }) => (
            <InputField
              additionalText='€ | Monat'
              label={getSubtitleRent()}
              hint='Die Warmmiete ist der Betrag, den du monatlich für deine Wohnung oder dein Haus zahlst – 
          inklusive Nebenkosten wie Heizkosten, Wasser und Müllabfuhr. 
          Sie entspricht der Gesamtmiete, die in deinem Mietvertrag angegeben ist.'
              type='text'
              inputMode='numeric'
              error={errors.housing_cost?.message}
              {...field}
              value={formatNumberWithThousandsSeparator(field.value)}
              onChange={(e) => field.onChange(stripToDigits(e.target.value))}
            />
          )}
        />
        <Controller
          name='additional_cost'
          control={control}
          render={({ field }) => (
            <InputField
              additionalText='€ | Monat'
              label='Sonstige Ausgaben'
              hint='Sonstige Ausgaben sind regelmäßige oder gelegentliche Kosten, die nicht zur Miete gehören – 
          zum Beispiel Versicherungen, Unterhaltsverpflichtungen, Kreditraten, Abonnements 
          oder monatliche Mitgliedsbeiträge.'
              optional
              placeholder='optional'
              type='text'
              inputMode='numeric'
              error={errors.additional_cost?.message}
              {...field}
              value={formatNumberWithThousandsSeparator(field.value)}
              onChange={(e) => field.onChange(stripToDigits(e.target.value))}
            />
          )}
        />
        {tabButton ? (
          <>
            {device === EDevice.MOBILE ? (
              <ButtonGroup
                className='back-next medium'
                type='back-next'
                data-testid='disabled-forward-button'
                appearOnFront
                buttonOneProps={{
                  type: 'button',
                  name: 'form-submit',
                  dataTestId: 'next',
                  children: 'nächster Schritt',
                  loading,
                  onClick: handleSubmit(submitData),
                }}
              />
            ) : (
              <div className='add-additional-person'>
                <Button
                  theme='secondary'
                  type='button'
                  className='save-button next button-text save-and-next-tab'
                  role='presentation'
                  loading={loading}
                  onClick={handleSubmit(submitData)}
                >
                  nächster Schritt
                </Button>
              </div>
            )}
          </>
        ) : (
          <ButtonGroup
            type='back-next'
            className='back-next'
            buttonOneProps={{
              type: 'submit',
              name: 'form-submit',
              loading,
              dataTestId: 'start-fetching',
              children: 'weiter',
            }}
          />
        )}
      </FormLayout>
    </form>
  );
};

export default IncomeData;
